/**
 * Formats an upload date into a human-readable relative time string,
 * e.g. "1 day ago", "2 weeks ago", "1 month ago", "2 years ago".
 *
 * @param uploadedAt - ISO 8601 date string or Date object of the upload time
 * @returns The formatted relative time string
 */
export function formatUploadedDate(uploadedAt: string | Date): string {
  const uploadDate =
    typeof uploadedAt === 'string' ? new Date(uploadedAt) : uploadedAt;
  const now = new Date();
  const diffMs = now.getTime() - uploadDate.getTime();
  const msPerDay = 1000 * 60 * 60 * 24;
  let diffDays = Math.floor(diffMs / msPerDay);

  // Ensure at least "1 day ago"
  if (diffDays < 1) {
    diffDays = 1;
  }

  // Days ago
  if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }

  // Weeks ago
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  }

  // Months ago (approximate: 30 days)
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }

  // Years ago (approximate: 365 days)
  const years = Math.floor(diffDays / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

// Example usage:
// console.log(formatUploadedDate('2025-05-19T12:00:00Z')); // '1 day ago'
// console.log(formatUploadedDate(new Date(Date.now() - 90 * msPerDay))); // '3 months ago'
